import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ExternalLink, Maximize2 } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Rectangle,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescriptionHidden,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { MetricCombobox } from '@/components/custom/metrics/metric-combobox'
import { formatCurrency } from '@/lib/format'
import {
  useExpandedMetricSeries,
  type ExpandedPeriodType,
  type MetricPoint,
  type FinancialMetricOption,
  type CompanyMetricSeries,
} from '@/hooks/use-metrics'
import { getFinancialChangeDisplay } from '@/features/company-overview/metric-card-model'

export type ExpandedSeriesFetcher = (
  metricLabelId: string | undefined,
  periodType: ExpandedPeriodType,
  enabled: boolean
) => {
  series: CompanyMetricSeries | undefined
  isFetching: boolean
}

interface FinancialStatCardProps {
  companyId: string
  title: string
  selectedMetricLabelId?: string
  disabledMetricLabelIds?: string[]
  metricOptions: FinancialMetricOption[]
  onSelectMetric: (metricLabelId: string) => void
  value: number | null
  changePercent: number | null
  changeDisplay: CompanyMetricSeries['changeDisplay']
  comparisonQuarter?: string | null
  comparisonValue?: number | null
  points: MetricPoint[]
  isLoading?: boolean
  expandedSeriesFetcher?: ExpandedSeriesFetcher
  onNavigateToFinancials?: (selectedPeriod: ExpandedPeriodType) => void
}

type ChartDatum = {
  quarter: string
  value: number
  isLatest: boolean
}

const chartConfig = {
  value: {
    label: 'Value',
    color: '#5f7fa1',
  },
} satisfies ChartConfig

const CARD_HOVER_SHADOW =
  '0px_5px_11px_0px_rgba(10,27,47,0.1),0px_20px_20px_0px_rgba(10,27,47,0.09),0px_46px_27px_0px_rgba(10,27,47,0.05),0px_81px_32px_0px_rgba(10,27,47,0.01),0px_127px_35px_0px_rgba(10,27,47,0)'

const COLLAPSED_GRIDLINE_COUNT = 7
const COLLAPSED_GRIDLINE_STROKE =
  'color-mix(in srgb, var(--primary) 10%, transparent)'

const NICE_BASES = [1, 2, 5, 10]

function niceCeilPositive(value: number): number {
  const abs = Math.abs(value)
  if (!Number.isFinite(abs) || abs === 0) return 0

  const exponent = Math.floor(Math.log10(abs))
  const base = abs / 10 ** exponent
  const niceBase = NICE_BASES.find((b) => b >= base) ?? 10
  return niceBase * 10 ** exponent
}

function niceFloorNegative(value: number): number {
  const abs = Math.abs(value)
  if (!Number.isFinite(abs) || abs === 0) return 0

  const exponent = Math.floor(Math.log10(abs))
  const base = abs / 10 ** exponent
  const niceBase = NICE_BASES.find((b) => b >= base) ?? 10
  return -(niceBase * 10 ** exponent)
}

function linspace(min: number, max: number, count: number): number[] {
  if (count <= 0) return []
  if (count === 1) return [min]
  const step = (max - min) / (count - 1)
  return Array.from({ length: count }, (_, i) => min + step * i)
}

function normalizeTicks(ticks: number[]): number[] {
  return Array.from(new Set(ticks.map((tick) => Number(tick.toFixed(6)))))
}

function toChartData(points: MetricPoint[]): ChartDatum[] {
  const fallbackPoints =
    points.length > 0
      ? points
      : [
          { quarter: 'Q1', value: null },
          { quarter: 'Q2', value: null },
          { quarter: 'Q3', value: null },
          { quarter: 'Q4', value: null },
        ]

  return fallbackPoints.map((point, index) => ({
    quarter: point.quarter,
    value: point.value ?? 0,
    isLatest: index === fallbackPoints.length - 1,
  }))
}

function formatChangeBadge(
  changePercent: number | null,
  changeDisplay: CompanyMetricSeries['changeDisplay']
): {
  value: string
  variant: 'changeNeutral'
} {
  return {
    value: getFinancialChangeDisplay(changePercent, changeDisplay).value,
    variant: 'changeNeutral',
  }
}

function ChangeBadgeWithTooltip({
  changeBadge,
  changeTooltip,
  disabled = false,
}: {
  changeBadge: ReturnType<typeof formatChangeBadge>
  changeTooltip: { heading: string; prior: string; current: string } | null
  disabled?: boolean
}) {
  const badge = <Badge variant={changeBadge.variant}>{changeBadge.value}</Badge>

  if (!changeTooltip || disabled) return badge

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span tabIndex={0}>{badge}</span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6}>
        <div className="space-y-0.5">
          <div className="font-medium">{changeTooltip.heading}</div>
          <div className="tabular-nums">
            <div>Prior: {changeTooltip.prior}</div>
            <div>Current: {changeTooltip.current}</div>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function FinancialStatTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value?: unknown }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  const value = payload[0]?.value
  const numericValue = typeof value === 'number' ? value : null

  return (
    <ChartTooltipContent className="pointer-events-none border-border/40 px-2 py-1 shadow-lg">
      <div className="flex items-center justify-between gap-4">
        <span className="text-primary/70">{label}</span>
        <span className="text-primary font-medium tabular-nums">
          {formatCurrency(numericValue)}
        </span>
      </div>
    </ChartTooltipContent>
  )
}

function buildChangeTooltip(
  quarter: string | null | undefined,
  prior: number | null | undefined,
  current: number | null
) {
  if (!quarter) return null
  return {
    heading: `YoY vs ${quarter}`,
    prior: formatCurrency(prior ?? null),
    current: formatCurrency(current),
  }
}

const PERIOD_TO_VIEW: Record<
  ExpandedPeriodType,
  'MONTHLY' | 'QUARTERLY' | 'ANNUAL'
> = {
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  LTM: 'QUARTERLY',
  YTD: 'ANNUAL',
}

const EXPANDED_PERIOD_OPTIONS: { value: ExpandedPeriodType; label: string }[] =
  [
    { value: 'MONTHLY', label: 'Month' },
    { value: 'QUARTERLY', label: 'Quarter' },
    { value: 'LTM', label: 'LTM' },
    { value: 'YTD', label: 'YTD' },
  ]

function getXAxisLabelInterval(dataLength: number): number {
  if (dataLength <= 8) return 0
  if (dataLength <= 12) return 1
  return 2
}

function CardChart({
  data,
  isExpanded = false,
}: {
  data: ChartDatum[]
  isExpanded?: boolean
}) {
  const [minValue, maxValue] = useMemo(() => {
    if (data.length === 0) return [0, 0] as const
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const d of data) {
      if (d.value < min) min = d.value
      if (d.value > max) max = d.value
    }
    if (!Number.isFinite(min)) min = 0
    if (!Number.isFinite(max)) max = 0
    return [min, max] as const
  }, [data])

  const collapsedScale = useMemo(() => {
    if (minValue === 0 && maxValue === 0) {
      return {
        domain: [0, 1] as const,
        gridTicks: linspace(0, 1, COLLAPSED_GRIDLINE_COUNT),
        labelValues: new Set<number>([0, 1]),
      }
    }

    // Collapsed cards should look like the design:
    // - more horizontal gridlines (avoid large white gaps)
    // - only label a few key values (min/0/max-ish)
    if (minValue < 0 && maxValue > 0) {
      const niceMax = niceCeilPositive(maxValue)
      const niceMin = niceFloorNegative(minValue)
      const posCount = Math.max(
        2,
        Math.round(COLLAPSED_GRIDLINE_COUNT * (niceMax / (niceMax - niceMin)))
      )
      const negCount = Math.max(2, COLLAPSED_GRIDLINE_COUNT - posCount + 1)
      const gridTicks = normalizeTicks([
        ...linspace(niceMin, 0, negCount),
        ...linspace(0, niceMax, posCount).slice(1),
      ])
      const labelValues = new Set<number>([niceMin, 0, niceMax])

      return {
        domain: [niceMin, niceMax] as const,
        gridTicks,
        labelValues,
      }
    }

    if (maxValue <= 0) {
      const niceMin = niceFloorNegative(minValue)
      const gridTicks = normalizeTicks(
        linspace(niceMin, 0, COLLAPSED_GRIDLINE_COUNT)
      )
      const labelValues = new Set<number>([niceMin, niceMin / 2, 0])

      return {
        domain: [niceMin, 0] as const,
        gridTicks,
        labelValues,
      }
    }

    const niceMax = niceCeilPositive(maxValue)
    const gridTicks = normalizeTicks(
      linspace(0, niceMax, COLLAPSED_GRIDLINE_COUNT)
    )
    const labelValues = new Set<number>([0, niceMax / 2, niceMax])

    return {
      domain: [0, niceMax] as const,
      gridTicks,
      labelValues,
    }
  }, [minValue, maxValue])

  const EXPANDED_TICK_COUNT = 5

  const expandedScale = useMemo(() => {
    if (minValue === 0 && maxValue === 0) {
      return {
        domain: [0, 1] as const,
        ticks: linspace(0, 1, EXPANDED_TICK_COUNT),
      }
    }

    if (minValue < 0 && maxValue > 0) {
      const niceMax = niceCeilPositive(maxValue)
      const niceMin = niceFloorNegative(minValue)
      const positiveTicks = Math.max(
        1,
        Math.round((EXPANDED_TICK_COUNT - 1) * (niceMax / (niceMax - niceMin)))
      )
      const negativeTicks = EXPANDED_TICK_COUNT - 1 - positiveTicks
      const ticks = normalizeTicks([
        ...linspace(niceMin, 0, negativeTicks + 1),
        ...linspace(0, niceMax, positiveTicks + 1).slice(1),
      ])
      return { domain: [niceMin, niceMax] as const, ticks }
    }
    if (maxValue <= 0) {
      const niceMin = niceFloorNegative(minValue)
      return {
        domain: [niceMin, 0] as const,
        ticks: normalizeTicks(linspace(niceMin, 0, EXPANDED_TICK_COUNT)),
      }
    }
    const niceMax = niceCeilPositive(maxValue)
    return {
      domain: [0, niceMax] as const,
      ticks: normalizeTicks(linspace(0, niceMax, EXPANDED_TICK_COUNT)),
    }
  }, [minValue, maxValue])

  const domain = isExpanded ? expandedScale.domain : collapsedScale.domain

  const positiveLatest = '#1a4878'
  const positivePast = '#5f7fa1'
  const negativeLatest = 'var(--secondary, #b86846)'
  const negativePast =
    'color-mix(in srgb, var(--secondary, #b86846) 65%, white)'

  return (
    <ChartContainer
      config={chartConfig}
      className={
        isExpanded
          ? 'h-72 w-full aspect-auto [&_.recharts-tooltip-cursor]:fill-transparent [&_.recharts-active-bar]:fill-transparent'
          : 'h-32 w-full aspect-auto [&_.recharts-tooltip-cursor]:fill-transparent [&_.recharts-active-bar]:fill-transparent'
      }
    >
      <BarChart
        data={data}
        margin={
          isExpanded
            ? { top: 8, right: 12, left: 12, bottom: 4 }
            : { top: 0, right: 2, left: 0, bottom: 0 }
        }
        barCategoryGap={isExpanded ? (data.length > 12 ? '10%' : '20%') : 8}
      >
        <CartesianGrid vertical={false} stroke={COLLAPSED_GRIDLINE_STROKE} />
        <ReferenceLine
          y={0}
          stroke="color-mix(in srgb, var(--primary) 14%, transparent)"
          strokeWidth={1}
        />
        <XAxis
          dataKey="quarter"
          axisLine={false}
          tickLine={false}
          tickMargin={6}
          interval={isExpanded ? getXAxisLabelInterval(data.length) : 0}
          minTickGap={0}
          tick={{
            fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
            fontSize: 10,
          }}
          tickFormatter={(value: string) =>
            isExpanded ? value : (value.split(' ')[0] ?? value)
          }
        />
        {isExpanded ? (
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
              fontSize: 10,
            }}
            tickFormatter={(v: number) => formatCurrency(v)}
            width={60}
            domain={domain}
            ticks={expandedScale.ticks}
          />
        ) : (
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fill: 'color-mix(in srgb, var(--primary) 45%, transparent)',
              fontSize: 10,
            }}
            tickFormatter={(value: number) =>
              collapsedScale.labelValues.has(value) ? formatCurrency(value) : ''
            }
            width={44}
            domain={domain}
            ticks={collapsedScale.gridTicks}
          />
        )}
        <ChartTooltip content={<FinancialStatTooltip />} />
        <Bar
          dataKey="value"
          maxBarSize={isExpanded ? 72 : 56}
          shape={<Rectangle radius={[4, 4, 0, 0]} />}
        >
          {data.map((entry) => (
            <Cell
              key={`${entry.quarter}-${entry.isLatest ? 'latest' : 'past'}`}
              fill={
                entry.value < 0
                  ? entry.isLatest
                    ? negativeLatest
                    : negativePast
                  : entry.isLatest
                    ? positiveLatest
                    : positivePast
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  )
}

export function FinancialStatCard({
  companyId,
  title,
  selectedMetricLabelId,
  disabledMetricLabelIds = [],
  metricOptions,
  onSelectMetric,
  value,
  changePercent,
  changeDisplay,
  comparisonQuarter = null,
  comparisonValue = null,
  points,
  isLoading = false,
  expandedSeriesFetcher,
  onNavigateToFinancials,
}: FinancialStatCardProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [selectedPeriod, setSelectedPeriod] =
    useState<ExpandedPeriodType>('LTM')

  const handleDialogChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) setSelectedPeriod('LTM')
  }

  const handleViewInFinancials = () => {
    if (onNavigateToFinancials) {
      onNavigateToFinancials(selectedPeriod)
      setOpen(false)
      return
    }
    void navigate({
      to: '/companies/$companyId',
      params: { companyId },
      search: (prev) => ({
        ...prev,
        tab: 'financials' as const,
        trailingPeriod: selectedPeriod,
        view: PERIOD_TO_VIEW[selectedPeriod],
      }),
    })
    setOpen(false)
  }

  const defaultExpanded = useExpandedMetricSeries(
    companyId,
    selectedMetricLabelId,
    selectedPeriod,
    open && !expandedSeriesFetcher
  )
  const customExpanded = expandedSeriesFetcher?.(
    selectedMetricLabelId,
    selectedPeriod,
    open
  )
  const { series: expandedSeries, isFetching: isExpandedFetching } =
    customExpanded ?? defaultExpanded

  const expandedPoints = expandedSeries?.points ?? points
  const expandedValue = expandedSeries?.latestValue ?? value
  const expandedChangePercent = expandedSeries?.changePercent ?? changePercent
  const expandedChangeDisplay = expandedSeries?.changeDisplay ?? changeDisplay

  const chartData = useMemo(() => toChartData(points), [points])
  const expandedChartData = useMemo(
    () => toChartData(expandedPoints),
    [expandedPoints]
  )
  const changeBadge = useMemo(
    () => formatChangeBadge(changePercent, changeDisplay),
    [changePercent, changeDisplay]
  )
  const expandedChangeBadge = useMemo(
    () => formatChangeBadge(expandedChangePercent, expandedChangeDisplay),
    [expandedChangePercent, expandedChangeDisplay]
  )
  const changeTooltip = useMemo(
    () => buildChangeTooltip(comparisonQuarter, comparisonValue, value),
    [comparisonQuarter, comparisonValue, value]
  )
  const expandedChangeTooltip = useMemo(
    () =>
      buildChangeTooltip(
        expandedSeries?.comparisonQuarter ?? comparisonQuarter,
        expandedSeries?.comparisonValue ?? comparisonValue,
        expandedValue
      ),
    [expandedSeries, comparisonQuarter, comparisonValue, expandedValue]
  )
  if (isLoading) {
    return <Skeleton className="h-[248px] w-full rounded-sm" />
  }

  return (
    <>
      <article
        className={`min-h-62 rounded-sm border border-border bg-white p-4 transition-[box-shadow,border-color] duration-200 ease-out hover:border-primary hover:shadow-[${CARD_HOVER_SHADOW}]`}
      >
        <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
          <MetricCombobox
            options={metricOptions}
            value={selectedMetricLabelId}
            onValueChange={onSelectMetric}
            disabledOptionIds={disabledMetricLabelIds}
            placeholder={title}
            variant="ghost"
            size="compact"
            className="h-7 min-w-0 flex-1 max-w-full rounded-sm px-2 py-1 text-sm"
            contentClassName="min-w-64"
            showFullValueTooltip
          />
          <Button
            variant="ghost"
            size="icon-xs"
            className="shrink-0 rounded-sm text-primary/70"
            onClick={() => setOpen(true)}
            aria-expanded={open}
            aria-label={`Expand ${title} chart`}
          >
            <Maximize2 />
          </Button>
        </div>

        <div className="mb-12 flex items-center gap-1.5">
          <Typography variant="metric-value" className="text-primary">
            {formatCurrency(value)}
          </Typography>
          <ChangeBadgeWithTooltip
            changeBadge={changeBadge}
            changeTooltip={changeTooltip}
            disabled={open}
          />
        </div>

        <CardChart data={chartData} />
      </article>

      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-3xl">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescriptionHidden>
            Expanded view of the {title} chart.
          </DialogDescriptionHidden>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Typography variant="metric-value" className="text-primary">
                  {formatCurrency(expandedValue)}
                </Typography>
                <ChangeBadgeWithTooltip
                  changeBadge={expandedChangeBadge}
                  changeTooltip={expandedChangeTooltip}
                />
                {isExpandedFetching && (
                  <div className="ml-2 h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary/60" />
                )}
              </div>
              <Select
                value={selectedPeriod}
                onValueChange={(v: string) => {
                  const opt = EXPANDED_PERIOD_OPTIONS.find((o) => o.value === v)
                  if (opt) setSelectedPeriod(opt.value)
                }}
              >
                <SelectTrigger className="h-8 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPANDED_PERIOD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {expandedPoints.length === 0 && !isExpandedFetching ? (
              <div className="flex h-72 items-center justify-center">
                <Typography variant="body-sm" className="text-primary/70">
                  No data available for this period type
                </Typography>
              </div>
            ) : (
              <CardChart data={expandedChartData} isExpanded />
            )}
            <div className="flex justify-end pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs text-primary/70 hover:text-primary"
                onClick={handleViewInFinancials}
              >
                View in Financials
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
